<template>
  <div class="flex flex-col sm:flex-row flex-nowrap gap-2 items-stretch sm:items-center justify-between mx-3 my-2 sm:my-4">
    <CheckoutBreadcrumbs :current="currentScreen" />
    <div class="flex gap-2 justify-between sm:justify-end items-center">
      <div class="flex items-center gap-2 justify-between w-full px-1">
        <Menu
          as="div"
          class="relative inline-block text-vp-base-500"
        >
          <MenuButton class="flex items-center gap-1 text-black">
            <iconify-icon
              icon="mdi:earth"
              class="text-xl"
            />
            {{ locale }}
            <iconify-icon
              icon="mdi:chevron-down"
              class="text-xl"
            />
          </MenuButton>

          <MenuItems class="absolute left-0 mt-2 bg-white shadow-lg ring-1 divide-y divide-gray-100 ring-black/5 focus:outline-hidden z-50">
            <div class="">
              <MenuItem
                v-for="(language, localeKey) in availableLocales"
                :key="localeKey"
              >
                <button
                  type="button"
                  class="px-3 py-1 group flex w-full hover:bg-vp-base-200"
                  @click="switchLanguage(String(localeKey))"
                >
                  {{ language }}
                </button>
              </MenuItem>
            </div>
          </MenuItems>
        </Menu>
        <div class="flex">
          <!-- eslint-disable vue/attribute-hyphenation vue/v-on-event-hyphenation -->
          <add-to-calendar-button
            v-if="event"
            options="'Apple','Google','iCal','Outlook.com','Microsoft365','Yahoo'"
            :label="calendarLabel"
            :name="event.name"
            :description="calendarDescription"
            :startDate="startDate"
            :startTime="startTime"
            :endDate="endDate"
            :endTime="endTime"
            :timezone="timezone"
            :location="venueAddress"
            buttonStyle="default"
            hideCheckmark="true"
            hideBranding="true"
            size="2|2|2"
            hideBackground="true"
            trigger="click"
          />
          <!-- eslint-enable vue/attribute-hyphenation vue/v-on-event-hyphenation -->
          <button
            type="button"
            class="py-2 flex gap-2 items-center cursor-pointer"
            @click.prevent="onShare"
          >
            <iconify-icon
              icon="mdi:share-variant-outline"
              class="text-xl"
            />
            {{ shareLabel }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import CheckoutBreadcrumbs, { type BreadcrumbKey } from './CheckoutBreadcrumbs.vue';
import copy from 'copy-to-clipboard';
import { useToast } from 'vue-toastification';
import { useGettext } from 'vue3-gettext';
import { useShare, useWindowSize } from '@vueuse/core';
import { useCheckoutStore } from './stores/checkout';
import { computed } from 'vue';
import * as Sentry from '@sentry/vue';
import 'add-to-calendar-button';
import { DateTime } from 'luxon';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue';
import { useLanguageStore } from './languageStore';
import gettext from './gettext';

type Props = {
  currentScreen: BreadcrumbKey;
};

defineProps<Props>();

const { $gettext } = useGettext();
const toast = useToast();
const { share, isSupported: isShareSupported } = useShare();
const checkoutStore = useCheckoutStore();
const languageStore = useLanguageStore();

const locale = computed(() => languageStore.current);
const availableLocales = gettext.available;

const event = computed(() => checkoutStore.getSelectedEvent);

const shareLabel = computed(() => isShareSupported.value ? $gettext('Share') : $gettext('Copy Link'));

const { width } = useWindowSize();
const calendarLabel = computed(() => width.value < 640 ? $gettext('Add to cal') : $gettext('Add to calendar'));

const onShare = async () => {
  const urlObj = new URL(window.location.href);

  urlObj.searchParams.set('utm_source', 'share');
  urlObj.searchParams.set('utm_medium', 'copy_link');
  urlObj.searchParams.set('utm_campaign', 'event_checkout');
  urlObj.searchParams.set('utm_content', 'top_right_button');

  const utmUrl = urlObj.href;

  if (!isShareSupported.value) {
    copy(utmUrl);
    toast.success($gettext('Event link copied to clipboard!'));
    return;
  }

  const eventName = event.value?.name;

  try {
    if (!eventName) {
      throw new Error('No event selected');
    }

    await share({
      title: eventName,
      text: $gettext('Check out this event on VenuePilot: '),
      url: utmUrl,
    });
  } catch (_e: any) {
    const e: DOMException = _e;
    if (e.name == 'AbortError') {
      return;
    }

    toast.error($gettext('Failed to share event'));
    Sentry.captureException(e, {
      extra: {
        context: 'Error sharing event',
        url: utmUrl,
      },
    });
  }
};

const timezone = computed(() => event.value?.venue?.timeZone);

// These feed the add-to-calendar button, which is behind `v-if="event"`. They stay
// undefined rather than asserting, so nothing here can fail the header's render.
const isoDateTime = (value: string | undefined) => (value ? DateTime.fromISO(value, { zone: timezone.value }) : undefined);

const normalDoorTime = computed(() => isoDateTime(event.value?.doorTime));
const normalEndTime = computed(() => isoDateTime(event.value?.endTime));

const startDate = computed(() => normalDoorTime.value?.toFormat('yyyy-MM-dd'));
const startTime = computed(() => normalDoorTime.value?.toFormat('HH:mm'));
const endDate = computed(() => normalEndTime.value?.toFormat('yyyy-MM-dd'));
const endTime = computed(() => normalEndTime.value?.toFormat('HH:mm'));

const venueAddress = computed(() => {
  const venue = event.value?.venue;
  if (venue) {
    return `${venue.name}, ${venue.street1}, ${venue.city}, ${venue.state}`;
  } else {
    return '';
  }
});

const calendarDescription = computed(() => {
  return $gettext('Purchase tickets or view event details here: %{link}', {
    link: event.value?.canonicalUrl || '',
  });
});

const switchLanguage = (locale: string) => {
  const url = new URL(window.location.href);
  void checkoutStore.changeLanguage(locale);
  url.searchParams.set('lang', locale);
  window.history.pushState({}, '', url.href);
};
</script>
